/* eslint-disable no-plusplus */

import {
  IBlackBoxInventoryQueryParams,
  IBulkScanInventoriesPayload,
  IFetchEvaluationDetailsParams,
  IFetchEvaluationImagesConfigurationParams,
  IFetchEvaluationListParams,
  IFetchNlStaffUrlOptions,
  IFetchPodDetailUrlOptions,
  IFetchSearchListUrlOptions,
  IFetchTransferDetailsParams,
  IGetInboundHistoryListPayload,
  IGetInboundZoneInventoriesPayload,
  IGetInventoryInfoPayload,
  IGetMissingInventoryListPayload,
  IGetPodInventoryListPayload,
  IGetTransferTraysParams,
  IInitiateCancelTransferPayload,
  IMissingPackageDetailsPayload,
  IPendencyDetailsPayload,
  IPendencyInventoryDetailsPayload,
  IResultPackageSummaryPayload,
  IScanTrayPodTransferCancelPayload,
  ISearchPackageResultPayload,
  ISearchTransferPackagesPayload,
  ISearchTransferResultPayload,
  ISearchTrayPackagesPayload,
  ISearchTraysResultPayload,
  ISourceDetailsParams,
  ITransferStatsParams,
  IUseTransferMissingTraysQueryParams,
  IUseTransferPendenciesTraysQueryParams,
} from './types';

/**
 * endpoint to get the scan options for the inventory.
 */
export const inventoryScanOptionsUrl = () => '/inventory/scan-options';

/**
 * endpoint to scan a package in order to move it to blackbox.
 */
export const scanToRemovePackageUrl = () => '/blackbox/scan';

/**
 * Endpoint for scanning a tray in a pod or a blackbox.
 */
export const scanTrayUrl = () => '/tray/scan';

/**
 * Endpoint for scanning a pod or a blackbox.
 */
export const scanPodUrl = () => '/pod/scan';

/**
 * endpoint to scan a package in order to add it to an inbound transaction.
 */
export const scanToAddPackageUrl = () => '/inventory/scan';

/**
 * endpoint to list inventories inside blackbox.
 */
export const fetchBlackBoxInventoriesUrl = (
  queryOptions: IBlackBoxInventoryQueryParams
) => {
  const params = new URLSearchParams();
  const { inventoryTransactionId, limit, page, search } = queryOptions;

  if (search) {
    params.append('search', search);
  }

  if (inventoryTransactionId) {
    params.append('inventoryTransactionId', inventoryTransactionId);
  }

  if (limit) {
    params.append('limit', limit.toString());
  }

  if (page) {
    params.append('page', page.toString());
  }

  return `/blackbox/inventories?${params.toString()}`;
};

/**
 * endpoint to scan a package in order to move it from blackbox to an inbound
 * transaction.
 */
export const scanToMovePackageFromBlackboxUrl = (
  inventoryTransactionId: string
) => `/blackbox/add-to-transaction/${inventoryTransactionId}`;

/**
 * Endpoint to get the info of a transaction like inventories, source details
 * from the transaction id bound withing an inbound transaction.
 */
export const getInventoryInfoUrl = (options: IGetInventoryInfoPayload) => {
  const { inventoryTransactionId, ...queryOptions } = options;

  const params = new URLSearchParams();

  Object.entries(queryOptions).forEach(([key, value]) => {
    if (value !== undefined) {
      params.append(
        key,
        Array.isArray(value) ? value.join(',') : String(value)
      );
    }
  });

  return `/inbound/list-inventories/${inventoryTransactionId}?${params.toString()}`;
};

/**
 * endpoint to list the source details.
 */
export const getSourceDetailsUrl = ({
  limit,
  page,
  sourceId,
}: ISourceDetailsParams) =>
  `/inventory/source-of-supply/${sourceId}?page=${page}&limit=${limit}`;

/**
 * Endpoint to get the info of a transaction like inventories, source details
 * from the transaction id bound withing an inbound transaction.
 */
export const getPackageListUrl = ({
  endDate,
  limit,
  location,
  page,
  search,
  searchFields,
  searchKeywords,
  sort,
  startDate,
}: ISearchPackageResultPayload) => {
  const params = new URLSearchParams();

  if (searchKeywords) {
    for (let i = 0; i < searchKeywords.length; i++) {
      const keyword = searchKeywords[i];

      if (keyword) {
        params.append('searchKeywords', keyword);
      }
    }
  }

  // If search key is exist then only add the search fields. Otherwise api will
  // give empty data.
  if (search && searchFields) {
    for (let i = 0; i < searchFields.length; i++) {
      const field = searchFields[i];

      if (field) {
        params.append('searchFields', field);
      }
    }
  }

  if (startDate) {
    params.append('startDate', startDate);
  }

  if (endDate) {
    params.append('endDate', endDate);
  }

  if (search) {
    params.append('search', search);
  }

  if (limit) {
    params.append('limit', limit.toString());
  }

  if (page) {
    params.append('page', page.toString());
  }

  if (location) {
    params.append('location', location);
  }

  if (sort) {
    params.append('sort', sort);
  }

  return `/inventory/search?${params.toString()}`;
};

/**
 * Endpoint to get the Pendency report.
 */
export const getPendencyReportUrl = () => '/pendencies/report';

/**
 * Endpoint to get the Pendency filter options.
 */
export const getPendencyFilterOptionsUrl = () => '/pendencies/filter-options';

/**
 * Endpoint to get the Package summary info.
 */
export const getPackageSummaryUrl = ({
  inventoryId,
}: IResultPackageSummaryPayload) => `/inventory/summary/${inventoryId}`;

/**
 * Endpoint to get the Package summary info.
 */
export const getPackageDetailsUrl = ({
  appraisalCode,
  inventoryId,
}: IResultPackageSummaryPayload) => {
  if (appraisalCode) {
    return `/pendencies/inflow/appraisal/${appraisalCode}`;
  }

  return `/inventory/info/${inventoryId}`;
};

export const getMissingPackageDetailsUrl = ({
  missingInventoryId,
}: IMissingPackageDetailsPayload) =>
  `/inventory/missing-inventories/${missingInventoryId}`;

/**
 * Endpoint to get the Package summary info.
 * Note: We need at least one filter to fetch the data otherwise api will through an error.
 */
export const getPendencyDetailsUrl = ({
  limit,
  page,
  filterKey,
  inflowFilterType,
  isToday,
  podType,
}: IPendencyDetailsPayload) => {
  const params = new URLSearchParams();

  if (limit) {
    params.append('limit', limit.toString());
  }

  if (page) {
    params.append('page', page.toString());
  }

  if (filterKey) {
    params.append('filterKey', filterKey);
  }

  if (inflowFilterType) {
    params.append('inflowFilterType', inflowFilterType);
  }

  if (podType) {
    params.append('podType', podType);
  }

  const pendencyPath = isToday ? 'TODAY_INFLOW' : 'YESTERDAY_INBOUND';

  return `/pendencies/inflow/${pendencyPath}?${params.toString()}`;
};

/**
 * Endpoint to get the Pendency filter options.
 */
export const getWeeklyStatsInfoUrl = () => '/pendencies/weekly-stats';

// -----------------------------------------------------------------------------
// ------------------------ 📝 INVENTORY MANAGEMENT ENDPOINTS ------------------------
// -----------------------------------------------------------------------------

/**
 * endpoint to list the NL Staff List.
 */
export const getNlStaffUrl = ({
  count,
  page,
  search,
}: IFetchNlStaffUrlOptions) =>
  `/user/inbound-nl-staffs?q=${search}&page=${page}&limit=${count}`;

/**
 * endpoint to list the Inbound Search List.
 */
export const fetchInboundListUrl = (options: IFetchSearchListUrlOptions) => {
  const params = new URLSearchParams();

  Object.entries(options).forEach(([key, value]) => {
    if (value !== undefined) {
      params.append(
        key,
        Array.isArray(value) ? value.join(',') : String(value)
      );
    }
  });

  return `/inbound?${params.toString()}`;
};

/**
 * endpoint to list the Inbound List Filter Options.
 */
export const fetchInboundListFilterOptionsUrl = () =>
  '/inbound/filter-list-options';

/**
 * endpoint to list the Inventory List Filter Options.
 */
export const fetchInventoryListFilterOptionsUrl = () =>
  '/inventory/filter-options';

/**
 * endpoint to list the Inbound Courier Providers List.
 */
export const fetchInboundCourierProviderListUrl = () =>
  '/inbound/courier-providers';

/**
 * endpoint to list the Inbound Types List.
 */
export const fetchInboundTypesListUrl = () => '/inbound/types';

/**
 * endpoint to fetch the create Inbound URL.
 */
export const fetchCreateOrUpdateInboundUrl = () => '/inbound';

/**
 * The API endpoint to fetch the File Upload URL for the video or image to the S3 bucket.
 * The default is the public endpoint, but if the upload requires a secure
 * connection, the secure endpoint is used.
 */
export const fetchFileUploadUrl = (requiresSecureUpload: boolean) =>
  `/${requiresSecureUpload ? 'secure/files/upload' : 'utils/video'}`;

/**
 * endpoint to list the Inbound Status List.
 */
export const fetchInboundStatusListUrl = (
  options: IFetchSearchListUrlOptions
) => {
  const params = new URLSearchParams();

  Object.entries(options).forEach(([key, value]) => {
    if (value !== undefined) {
      params.append(
        key,
        Array.isArray(value) ? value.join(',') : String(value)
      );
    }
  });

  return `/inbound/stats?${params.toString()}`;
};

/**
 * The API endpoint to fetch the POD details with aging information.
 * @param podIdentifier - The ID of POD to fetch details for.
 */
export const fetchPodDetailsWithAgingInfoUrl = (
  queryParams: IFetchPodDetailUrlOptions
) => {
  const params = new URLSearchParams();
  const { zone, podIdentifier, ...queryOptions } = queryParams;

  Object.entries(queryOptions).forEach(([key, value]) => {
    if (value !== undefined) {
      params.append(
        key,
        Array.isArray(value) ? value.join(',') : String(value)
      );
    }
  });

  return `pod/${zone}/${podIdentifier}?${params.toString()}`;
};

/**
 * The API endpoint to fetch the POD inventory list.
 * @param podIdentifier - The ID of POD to fetch Inventory list for.
 */
export const fetchPodInventoryListUrl = (
  payload: IGetPodInventoryListPayload
) => {
  const params = new URLSearchParams();
  const { zone, podIdentifier, ...queryOptions } = payload;

  Object.entries(queryOptions).forEach(([key, value]) => {
    if (value !== undefined) {
      params.append(
        key,
        Array.isArray(value) ? value.join(',') : String(value)
      );
    }
  });

  return `pod/inventory/${zone}/${podIdentifier}?${params.toString()}`;
};

/**
 * The API endpoint to fetch the Inbound Detail Info.
 * @param inboundIdentifier - The ID of Inbound to fetch the details.
 * TODO: Delete this since it's not used.
 * @deprecated - Not used at all
 */
export const fetchInboundDetailsInfoUrl = (inboundIdentifier: string) =>
  `inbound/info/${inboundIdentifier}`;

/**
 * endpoint to fetch the close Inbound Transaction URL.
 */
export const closeInboundTransactionUrl = () => '/inbound/close';

/**
 * endpoint to fetch the Inbound History URL.
 */
export const inboundHistoryDetailsListInfoUrl = (
  options: IGetInboundHistoryListPayload
) => {
  const params = new URLSearchParams();

  Object.entries(options).forEach(([key, value]) => {
    if (value !== undefined) {
      params.append(
        key,
        Array.isArray(value) ? value.join(',') : String(value)
      );
    }
  });

  return `/inbound/history/${
    options.inventoryTransactionId
  }?${params.toString()}`;
};

/**
 * endpoint to fetch details of in house inventory.
 */
export const fetchInHouseInventoryUrl = () =>
  '/inventory/scan-in-house-inventory';

/**
 * Endpoint to fetch missing inventory list.
 */
export const fetchMissingInventoryListUrl = (
  payload: IGetMissingInventoryListPayload
) =>
  `/inventory/missing-inventories?search=${payload.search}&page=${
    payload.page
  }&limit=${payload.limit}&inventoryTransactionId=${
    payload.inventoryTransactionId ? payload.inventoryTransactionId : ''
  }`;

/**
 * Endpoint to get the Courier Info.
 */
export const getCourierInfoUrl = (inboundIdentifier: string) =>
  `/inbound/courier/${inboundIdentifier}`;

/**
 * Endpoint for updating the courier driver info.
 */
export const updateCourierDriverInfoUrl = () => '/inbound/courier-info';

/**
 * Endpoint for fetching the file using the signed URL.
 */
export const fetchFileFromSignedUrl = () => '/utils/file/signed-cdn-url';

/**
 * endpoint to archive an inbound transaction.
 */
export const archiveInboundUrl = () => '/inbound/archive';

/**
 * Endpoint to get the inventories of a zone.
 */
export const getInboundZoneInventoryListUrl = ({
  inventoryTransactionId,
  limit,
  page,
  zone,
}: IGetInboundZoneInventoriesPayload) =>
  `/inventory/list-inventories/${zone}/${inventoryTransactionId}?page=${page}&limit=${limit}`;

/**
 * Endpoint to get the tray initial locations.
 */
export const getInitialLocationsUrl = () => '/tray/initial-location';

/**
 * Endpoint to get the tray name list.
 */
export const generateTrayNameListUrl = () => '/tray';

/**
 * Endpoint to get the tray name list.
 */
export const createNewTraysUrl = () => '/tray/confirm';

/**
 * Endpoint to get last tray details.
 */
export const getLastTrayDetailsUrl = () => '/tray/last-created';

/**
 * Endpoint to get tray filter options.
 */
export const getTrayFilterUrl = () => '/tray/listing-filters';

/**
 * Endpoint to get tray list.
 */
export const getTraysUrl = ({
  limit,
  page,
  search,
  filter,
}: ISearchTraysResultPayload) => {
  const params = new URLSearchParams();

  if (page) {
    params.append('page', page.toString());
  }

  if (search) {
    params.append('search', search);
  }

  if (filter) {
    params.append('filter', filter);
  }

  return `/tray?limit=${limit}&${params.toString()}`;
};

/**
 * Endpoint to get tray trayDetails
 */
export const getTrayDetailsUrl = (trayNumber: string) =>
  `/tray/${trayNumber}/details?trayAgeingInfo=true`;

/**
 * Endpoint to get tray list.
 */
export const getTrayPackageListUrl = ({
  limit,
  page,
  search,
  trayId,
  ageFilter,
}: ISearchTrayPackagesPayload) => {
  const params = new URLSearchParams();

  if (page) {
    params.append('page', page.toString());
  }

  if (search) {
    params.append('search', search);
  }

  if (ageFilter) {
    params.append('ageFilter', ageFilter);
  }

  return `tray/${trayId}/inventories?limit=${limit}&${params.toString()}`;
};

/**
 * Endpoint to get pod locations.
 */
export const getPodLocationsUrl = (inventoryId: string) =>
  `inventory/available-pods/${inventoryId}`;

/**
 * This endpoint is used to get the list of field types that can be updated
 * for a specific inventory.
 */
export const fetchFieldToUpdateTypesListUrl = () =>
  '/inventory/available-fields-update';

/**
 * endpoint to fetch the update Inbound Package URL.
 */
export const fetchUpdateInboundPackageUrl = (inventoryId: string) =>
  `/inventory/update-inventory/${inventoryId}`;

/**
 * endpoint to empty a tray.
 */
export const emptyTrayUrl = (trayID: string) => `tray/${trayID}/temp/empty`;

/**
 * endpoint to scan a package in order to switch it to a pod.
 */
export const fetchScanPackageToSwitchPodUrl = () =>
  '/inventory/pod-switch/scan';

/**
 * Endpoint to get transfer list.
 */
export const getTransferListUrl = ({
  filter,
  inventoryTransferFilterType,
  limit,
  page,
  search,
}: ISearchTransferResultPayload) => {
  const params = new URLSearchParams();

  if (page) {
    params.append('page', page.toString());
  }

  if (inventoryTransferFilterType) {
    params.append('inventoryTransferFilterType', inventoryTransferFilterType);
  }

  if (search) {
    params.append('search', search);
  }

  if (filter) {
    params.append('InventoryTransferType', filter);
  }

  return `/inventory-transfer?limit=${limit}&${params.toString()}`;
};

/**
 * Endpoint to get transfer trays.
 */
export const getTransferTraysUrl = ({
  inventoryTransferId,
  limit,
  page,
  filter,
}: IGetTransferTraysParams) => {
  const params = new URLSearchParams();

  if (page) {
    params.append('page', page.toString());
  }

  if (limit) {
    params.append('limit', limit.toString());
  }

  if (filter) {
    params.append('filter', filter);
  }

  return `/inventory-transfer/${inventoryTransferId}/trays?${params.toString()}`;
};

/**
 * Endpoint to get the tray name list.
 */
export const getTransferFilterUrl = () => '/inventory-transfer/filter-options';

/**
 * endpoint to mark tray as missing
 */
export const getMarkTrayMissingUrl = () => '/tray/missing';

/**
 * endpoint to get the bulk scan inventories.
 */
export const getBulkScanInventoriesUrl = ({
  inventoryTransactionId,
  page,
  limit,
  filter,
}: IBulkScanInventoriesPayload) => {
  const params = new URLSearchParams();

  if (page) {
    params.append('page', page.toString());
  }
  if (limit) {
    params.append('limit', limit.toString());
  }
  if (filter) {
    params.append('filter', filter);
  }

  return `/inventory/bulk-scan-inventories/${inventoryTransactionId}?${params.toString()}`;
};

/**
 * Endpoint to mark an inventory as missing.
 */
export const getMarkInventoryAsMissingUrl = () =>
  '/inventory/missing-inventory';

/**
 * endpoint to list the evaluations list.
 */
export const getEvaluationsList = ({
  count,
  page,
  status,
  search,
}: IFetchEvaluationListParams) => {
  const path =
    status === 'IN_PROGRESS'
      ? `/evaluation?status=${status}&page=${page}&limit=${count}`
      : `/evaluation?status=${status}&page=${page}&limit=${count}&search=${search}`;

  return path;
};

/**
 *  Endpoint to get the evaluation image upload config url
 */
export const getEvaluationImageUploadConfigUrl = ({
  categoryType,
}: IFetchEvaluationImagesConfigurationParams) =>
  `/evaluation/configuration/${categoryType}`;

/**
 * Endpoint to scan a package for evaluation.
 */
export const scanPackageForEvaluationUrl = () => '/evaluation/scan';

/**
 * Endpoint to scan a port to create a new evaluation.
 */
export const scanPortForEvaluationUrl = () => '/evaluation/scan-port';

/**
 * Endpoint to scan a evaluated appraisal barCode, IMEI or appraisal code
 */
export const scanEvaluatedAppraisalUrl = () => '/evaluation/scan-evaluated';

/**
 * Endpoint to scan a port for existing evaluation.
 */
export const scanPortForExistingEvaluationUrl = () => '/evaluation/scan-device';

/**
 * Endpoint to add/delete media to an evaluation.
 */
export const addOrDeleteMediaToEvaluationUrl = () => '/evaluation/media';

/**
 * Endpoint to get the close the evaluation.
 */
export const closeEvaluationUrl = () => '/evaluation/finished';

/**
 * Endpoint to get transfer trayDetails
 */
export const getTransferDetailsUrl = ({
  inventoryTransferId,
  isTrays,
}: IFetchTransferDetailsParams) => {
  if (isTrays) {
    return `/inventory-transfer/${inventoryTransferId}/details?traysInInventoryTransfer=${isTrays}`;
  }

  return `/inventory-transfer/${inventoryTransferId}/details`;
};

/**
 * Endpoint to get transfer package list.
 */
export const getTransferPackageListUrl = ({
  limit,
  page,
  trayId,
  inventoryTransferId,
}: ISearchTransferPackagesPayload) => {
  const params = new URLSearchParams();

  if (page) {
    params.append('page', page.toString());
  }

  if (trayId) {
    params.append('trayId', trayId);
  }

  return `/inventory-transfer/${inventoryTransferId}/inventories?limit=${limit}&${params.toString()}`;
};

/**
 * Endpoint to get transfer stats.
 */
export const getTransferStatsUrl = (payload?: ITransferStatsParams) => {
  const { inventoryTransferId } = payload || {};

  if (inventoryTransferId) {
    return `/inventory-transfer/stats?inventoryTransferId=${inventoryTransferId}`;
  }

  return '/inventory-transfer/stats';
};

/**
 * Endpoint to get missing trays.
 */
export const getTransferMissingTraysUrl = ({
  limit,
  page = 1,
  search,
  inventoryTransferId,
}: IUseTransferMissingTraysQueryParams) => {
  const params = new URLSearchParams();

  if (page) {
    params.append('page', page.toString());
  }

  if (limit) {
    params.append('limit', limit.toString());
  }

  if (search) {
    params.append('search', search);
  }

  if (inventoryTransferId) {
    params.append('inventoryTransferId', inventoryTransferId);
  }

  return `/tray/missing?${params.toString()}`;
};

export const getEvaluationDetailsUrl = ({
  evaluationId,
}: IFetchEvaluationDetailsParams) => `/evaluation/${evaluationId}`;
/**
 * Endpoint for scanning tray orPod
 */
export const scanTrayOrPodUrl = (scanCode: string) =>
  `/inventory-transfer/${scanCode}/scan`;

/**
 * Endpoint for marking a tray as received
 */
export const markTrayAsReceivedUrl = () => '/tray/received';

/**
 * Endpoint for scanning destination zone
 */
export const scanDestinationZoneUrl = (
  scannedPodOrWs: string,
  inventoryTransferId: string
) => `/inventory-transfer/${scannedPodOrWs}/${inventoryTransferId}/scan`;

/**
 * Endpoint for confirming transfer
 */
export const confirmTransferUrl = (inventoryTransferId: string) =>
  `/inventory-transfer/${inventoryTransferId}/confirm`;

/**
 * Endpoint to get the transfer pendencies list.
 */
export const getTransferPendenciesListUrl = () =>
  'pendencies/inventory-transfer';

/**
 * Endpoint to get tray list.
 */
export const getTransferPendenciesPackagesUrl = ({
  limit,
  page,
  trayId,
  inventoryTransferType,
}: IPendencyInventoryDetailsPayload) => {
  const params = new URLSearchParams();

  if (trayId) {
    params.append('trayId', trayId);
  }

  return `pendencies/inventory-transfer/inventories/${inventoryTransferType}?page=${page}&limit=${limit}&${params.toString()}`;
};

/**
 * Endpoint for cancelling a tray or pod scan during transfer
 */
export const scanTrayPodTransferCancelUrl = ({
  scanCode,
  inventoryTransferId,
}: IScanTrayPodTransferCancelPayload) =>
  `/inventory-transfer/${scanCode}/${inventoryTransferId}/cancel-scan`;

/**
 * Endpoint for cancelling a tray or pod scan during transfer
 */
export const initiateCancelTransferUrl = ({
  inventoryTransferId,
}: IInitiateCancelTransferPayload) =>
  `/inventory-transfer/${inventoryTransferId}/cancel`;

/**
 * Endpoint to get missing trays.
 */
export const getTransferPendenciesTraysUrl = ({
  inventoryTransferFilterType,
}: IUseTransferPendenciesTraysQueryParams) =>
  `/pendencies/inventory-transfer/trays/${inventoryTransferFilterType}`;
